import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Users, Heart, GraduationCap, MapPin, Wallet, Moon, Cigarette, Utensils,
  Sparkles, ArrowRight, MessageCircle, CheckCircle2
} from "lucide-react";

const SLEEP = [
  { v: "early_bird", l: "Early bird" },
  { v: "night_owl", l: "Night owl" },
  { v: "flexible", l: "Flexible" },
];
const SMOKING = [
  { v: "no", l: "Non-smoker" },
  { v: "occasional", l: "Occasional" },
  { v: "yes", l: "Smoker" },
];
const FOOD = [
  { v: "veg", l: "Veg" },
  { v: "non_veg", l: "Non-veg" },
  { v: "both", l: "Both" },
];
const GENDERS = [
  { v: "male", l: "Male" },
  { v: "female", l: "Female" },
  { v: "co-ed", l: "Co-ed friendly" },
];
const INTEREST_OPTIONS = ["Gaming","Sports","Coding","Music","Movies","Fitness","Reading","Travel","Foodie","Photography"];

const Pill = ({ active, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider border transition ${
      active ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-gray-600 border-gray-200 hover:border-emerald-300"
    }`}>{children}</button>
);

export default function Roommates() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [pref, setPref] = useState({
    college: "", city: "", gender: "", budget_max: 8000,
    sleep_schedule: "flexible", smoking: "no", food: "both",
    cleanliness: 3, interests: [], bio: "",
  });
  const [matches, setMatches] = useState([]);
  const [step, setStep] = useState("form"); // form | matches
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user || typeof user !== "object") { nav("/login", { state: { from: "/roommates" }}); return; }
    api.get("/roommates/me").then(({data}) => {
      if (data && data.user_id) {
        setPref(p => ({ ...p, ...data }));
        loadMatches();
      }
    }).catch(()=>{});
    // eslint-disable-next-line
  }, [user, loading]);

  const loadMatches = async () => {
    try {
      const { data } = await api.get("/roommates/matches");
      setMatches(data || []);
      setStep("matches");
    } catch (e) {
      // Means no profile saved yet — keep on form
    }
  };

  const updateField = (k, v) => setPref(p => ({ ...p, [k]: v }));

  const save = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      await api.put("/roommates/me", pref);
      toast.success("Profile saved! Finding matches...");
      await loadMatches();
    } catch (e) {
      toast.error("Failed to save");
    } finally {
      setBusy(false);
    }
  };

  const startThread = async (otherUserId) => {
    try {
      const { data } = await api.post("/threads/start", { other_user_id: otherUserId });
      toast.success("Chat opened — say hi!");
      nav(`/messages?thread=${data.id}`);
    } catch (e) {
      toast.error("Couldn't start chat");
    }
  };

  const inputCls = "w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none transition text-sm";
  const labelCls = "text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5 block";

  return (
    <div className="bg-[#FBFAF6] min-h-screen" data-testid="roommates-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-gray-200">
        <div className="absolute inset-0 bg-gradient-to-br from-rose-50/60 via-[#FBFAF6] to-emerald-50/40 pointer-events-none"/>
        <div className="relative max-w-7xl mx-auto px-5 md:px-10 py-12 md:py-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-rose-100 border border-rose-200 text-rose-800 text-xs font-semibold uppercase tracking-wider">
            <Heart size={14}/> Roommate Finder
          </div>
          <h1 className="font-display font-black text-4xl md:text-6xl mt-4 tracking-tighter text-[#0E1F18]">
            Find your <span className="text-emerald-600">perfect</span> match.
          </h1>
          <p className="mt-3 text-lg text-[#4A4A4A] max-w-2xl">
            Tell us about yourself once. We&apos;ll show you the most compatible roommates by college, lifestyle, sleep schedule, food and more.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-10 py-10">
        {step === "form" ? (
          <form onSubmit={save} className="grid lg:grid-cols-[1fr_360px] gap-8" data-testid="pref-form">
            <div className="bg-white rounded-3xl border border-gray-200 p-6 md:p-8 space-y-6">
              <h2 className="font-display font-black text-2xl text-[#0E1F18]">Your profile</h2>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>City</label>
                  <input className={inputCls} value={pref.city} onChange={e=>updateField("city", e.target.value)} placeholder="Jaipur, Bengaluru..." data-testid="roommate-city"/>
                </div>
                <div>
                  <label className={labelCls}>College</label>
                  <input className={inputCls} value={pref.college} onChange={e=>updateField("college", e.target.value)} placeholder="Poornima, IIT, NIT..." data-testid="roommate-college"/>
                </div>
              </div>

              <div>
                <label className={labelCls}>Gender preference</label>
                <div className="flex flex-wrap gap-2">
                  {GENDERS.map(g => (
                    <Pill key={g.v} active={pref.gender === g.v} onClick={()=>updateField("gender", g.v)} testid={`pref-gender-${g.v}`}>{g.l}</Pill>
                  ))}
                </div>
              </div>

              <div>
                <label className={labelCls}>Budget (₹/month, max)</label>
                <input type="range" min="3000" max="20000" step="500" value={pref.budget_max} onChange={e=>updateField("budget_max", +e.target.value)} className="w-full accent-emerald-600" data-testid="roommate-budget-max"/>
                <div className="text-sm text-gray-600 mt-1">Up to <strong className="text-emerald-700">₹{pref.budget_max.toLocaleString()}</strong>/month</div>
              </div>

              <div>
                <label className={labelCls}>Sleep schedule</label>
                <div className="flex flex-wrap gap-2">
                  {SLEEP.map(s => (
                    <Pill key={s.v} active={pref.sleep_schedule === s.v} onClick={()=>updateField("sleep_schedule", s.v)}>{s.l}</Pill>
                  ))}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className={labelCls}>Smoking</label>
                  <div className="flex flex-wrap gap-2">
                    {SMOKING.map(s => (
                      <Pill key={s.v} active={pref.smoking === s.v} onClick={()=>updateField("smoking", s.v)}>{s.l}</Pill>
                    ))}
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Food preference</label>
                  <div className="flex flex-wrap gap-2">
                    {FOOD.map(s => (
                      <Pill key={s.v} active={pref.food === s.v} onClick={()=>updateField("food", s.v)}>{s.l}</Pill>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <label className={labelCls}>Cleanliness (1 = relaxed, 5 = neat freak)</label>
                <div className="flex gap-2">
                  {[1,2,3,4,5].map(n => (
                    <button type="button" key={n} onClick={()=>updateField("cleanliness", n)}
                      className={`flex-1 py-2 rounded-xl font-bold transition border ${
                        pref.cleanliness === n
                          ? "bg-emerald-600 text-white border-emerald-600"
                          : "bg-white text-gray-600 border-gray-200 hover:border-emerald-300"
                      }`} data-testid={`pref-clean-${n}`}>{n}</button>
                  ))}
                </div>
              </div>

              <div>
                <label className={labelCls}>Interests (pick a few)</label>
                <div className="flex flex-wrap gap-2">
                  {INTEREST_OPTIONS.map(i => (
                    <Pill key={i} active={pref.interests.includes(i)} onClick={()=>updateField("interests", pref.interests.includes(i) ? pref.interests.filter(x => x !== i) : [...pref.interests, i])}>{i}</Pill>
                  ))}
                </div>
              </div>

              <div>
                <label className={labelCls}>One-liner bio (optional)</label>
                <textarea className={`${inputCls} min-h-[80px]`} value={pref.bio} onChange={e=>updateField("bio", e.target.value)} placeholder="Quiet engineering student who loves chai and chess" data-testid="roommate-bio"/>
              </div>

              <button disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-semibold py-3.5 rounded-xl transition" data-testid="roommate-save">
                {busy ? "Saving..." : <>Save & find matches <Sparkles size={16}/></>}
              </button>
            </div>

            {/* Sidebar tips */}
            <aside className="space-y-3">
              <div className="bg-gradient-to-br from-emerald-600 to-teal-500 rounded-3xl p-6 text-white">
                <Sparkles size={28}/>
                <h3 className="font-display font-black text-xl mt-3">How matching works</h3>
                <ul className="mt-3 text-sm space-y-2 text-white/90">
                  <li className="flex gap-2"><CheckCircle2 size={16}/> Same college / city: bigger boost</li>
                  <li className="flex gap-2"><CheckCircle2 size={16}/> Sleep, food, smoking compatibility</li>
                  <li className="flex gap-2"><CheckCircle2 size={16}/> Shared interests + budget range</li>
                  <li className="flex gap-2"><CheckCircle2 size={16}/> Chat in-app — no phone numbers shared</li>
                </ul>
              </div>
              <div className="bg-white rounded-2xl border border-gray-200 p-5 text-sm">
                <div className="font-bold text-[#0E1F18]">🔒 Your data is private</div>
                <p className="text-gray-500 mt-1">Only your name + avatar are visible to potential matches. Bio + interests are optional.</p>
              </div>
            </aside>
          </form>
        ) : (
          <>
            <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
              <h2 className="font-display font-black text-3xl text-[#0E1F18]">{matches.length} matches found</h2>
              <button onClick={()=>setStep("form")} className="text-sm font-semibold text-emerald-700 hover:underline" data-testid="edit-prefs-btn">Edit preferences →</button>
            </div>

            {matches.length === 0 ? (
              <div className="bg-white rounded-3xl border border-gray-200 p-10 text-center" data-testid="no-matches">
                <Users size={42} className="mx-auto text-gray-300"/>
                <h3 className="font-display font-black text-2xl mt-3 text-[#0E1F18]">No matches yet</h3>
                <p className="text-gray-500 mt-2">Hostro is growing fast — invite friends to fill their roommate profile.</p>
                <Link to="/" className="inline-flex items-center gap-2 mt-5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-5 py-3 rounded-xl">Back home <ArrowRight size={16}/></Link>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="roommates-matches">
                {matches.map(m => (
                  <div key={m.user_id} className="bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition" data-testid={`match-${m.user_id}`}>
                    <div className="relative h-32 bg-gradient-to-br from-emerald-400 to-teal-500">
                      <div className="absolute top-3 right-3 bg-white/95 backdrop-blur rounded-full px-2.5 py-1 text-xs font-bold text-emerald-700">
                        {m.match_percent}% match
                      </div>
                      <div className="absolute -bottom-8 left-5">
                        <div className="w-16 h-16 rounded-full border-4 border-white bg-white grid place-items-center overflow-hidden">
                          {m.avatar_url ? <img src={m.avatar_url} alt="" className="w-full h-full object-cover"/> : (
                            <div className="w-full h-full bg-gradient-to-br from-rose-400 to-amber-400 grid place-items-center text-white font-bold text-xl">
                              {(m.name || "?")[0].toUpperCase()}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="pt-10 p-5">
                      <div className="font-display font-black text-lg text-[#0E1F18]">{m.name || "Anonymous"}</div>
                      <div className="text-xs text-gray-500 mt-0.5 space-y-0.5">
                        {m.college && <div className="flex items-center gap-1"><GraduationCap size={11}/>{m.college}</div>}
                        {m.city && <div className="flex items-center gap-1"><MapPin size={11}/>{m.city}</div>}
                      </div>
                      {m.bio && <p className="mt-3 text-sm text-gray-600 line-clamp-2">{m.bio}</p>}
                      <div className="mt-3 flex flex-wrap gap-1">
                        {m.budget_max ? <span className="inline-flex items-center gap-1 text-xs bg-emerald-50 border border-emerald-200 text-emerald-700 px-2 py-0.5 rounded-full"><Wallet size={10}/>₹{(+m.budget_max).toLocaleString()}</span> : null}
                        {m.sleep_schedule && <span className="inline-flex items-center gap-1 text-xs bg-amber-50 border border-amber-200 text-amber-700 px-2 py-0.5 rounded-full"><Moon size={10}/>{m.sleep_schedule.replace("_"," ")}</span>}
                        {m.food && <span className="inline-flex items-center gap-1 text-xs bg-rose-50 border border-rose-200 text-rose-700 px-2 py-0.5 rounded-full"><Utensils size={10}/>{m.food}</span>}
                        {m.smoking && m.smoking !== "no" && <span className="inline-flex items-center gap-1 text-xs bg-gray-50 border border-gray-200 text-gray-700 px-2 py-0.5 rounded-full"><Cigarette size={10}/>{m.smoking}</span>}
                      </div>
                      <button onClick={()=>startThread(m.user_id)} className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-2.5 rounded-xl text-sm transition" data-testid={`chat-match-${m.user_id}`}>
                        <MessageCircle size={14}/> Say hi
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
